import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { ArrowLeft } from "lucide-react";
import { SiteSelect } from "@/components/SiteSelect";

export default function SupervisorServices() {
  const [clients, setClients] = useState([]);
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("open");
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const status = tab === "all" ? "" : `?status=${tab}`;
      const [c, s] = await Promise.all([api.get("/clients"), api.get(`/services${status}`)]);
      setClients(c.data); setList(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openSvc = async (takeover = false) => {
    if (!clientId) return toast.error("Elegí un cliente");
    setBusy(true);
    try {
      await api.post("/services/open", { client_id: clientId, site_id: siteId || null, takeover });
      toast.success(takeover ? "Relevo tomado: servicio abierto" : "Servicio abierto");
      setClientId(""); setSiteId(""); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "service_already_open") {
        if (window.confirm(`${detail.msg}\n\n¿Tomar el relevo ahora?`)) return openSvc(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  const closeSvc = async (id) => {
    setBusy(true);
    try { await api.post("/services/close", { service_id: id }); toast.success("Servicio cerrado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const nameOf = (cid) => clients.find((c) => c.id === cid)?.name || "—";

  return (
    <div style={{ padding: "8px 16px" }}>
      <Link to="/supervisor" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A", letterSpacing: "-0.02em" }} data-testid="services-open-title">Servicios Abiertos</h1>

      <div className="card" style={{ marginBottom: 18, padding: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>Abrir nuevo servicio</div>
        <div className="field"><label>Cliente</label>
          <select value={clientId} onChange={(e) => { setClientId(e.target.value); setSiteId(""); }} data-testid="svc-open-client">
            <option value="">Elegir cliente...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <SiteSelect clientId={clientId} value={siteId} onChange={setSiteId} testIdPrefix="svc"/>
        <button className="btn-primary" onClick={() => openSvc(false)} disabled={busy || !clientId} style={{ width: "100%", padding: 12 }} data-testid="svc-open-submit">Abrir servicio</button>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 14, borderBottom: "1px solid var(--invict-bone-3)" }}>
        {[
          { k: "open", label: "Abiertos" },
          { k: "closed", label: "Cerrados" },
          { k: "all", label: "Todos" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding: "10px 16px", border: "none", background: "transparent", color: tab === t.k ? "#0A0A0A" : "var(--invict-text-muted)", fontWeight: 700, fontSize: 12, letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer", borderBottom: tab === t.k ? "3px solid #BF3437" : "3px solid transparent" }}
            data-testid={`tab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {list.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin servicios en este estado.</div>}
        {list.map((s) => (
          <div key={s.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }} data-testid={`svc-row-${s.id}`}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14 }}>{nameOf(s.client_id)}{s.site_name ? ` · ${s.site_name}` : ""}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                {s.status === "open" ? "Abierto" : "Cerrado"} por {s.supervisor_name}{s.supervisor_deleted && <DeletedUserBadge id={s.id} />} · {new Date(s.opened_at).toLocaleString("es-AR")}
                {s.closed_at && ` · Cierre: ${new Date(s.closed_at).toLocaleString("es-AR")}${s.auto_closed ? " (automático)" : ""}`}
              </div>
            </div>
            {s.status === "open" && <button onClick={() => closeSvc(s.id)} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11, borderColor: "#BF3437", color: "#BF3437" }} data-testid={`svc-close-${s.id}`}>Cerrar</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
